import { useAuth } from "@clerk/nextjs";
import type { JobSalaryClient } from "@lossdog/job-watchlists";
import { StreamingJobSalaryClient } from "@lossdog/job-watchlists";
import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { AppConfig } from "@/config/app.config";
import { logger } from "@/util/logger";

interface JobSalaryContextType {
  // Client and connection only
  client: JobSalaryClient | null;
  isLoading: boolean;
}

const JobSalaryContext = createContext<JobSalaryContextType | undefined>(undefined);

export const JobSalaryProvider = ({ children }: { children: ReactNode }) => {
  // Client state only
  const { getToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [client, setClient] = useState<JobSalaryClient | null>(null);

  // Initialize client
  useEffect(() => {
    let mounted = true;
    setIsLoading(true);

    async function initClient() {
      try {
        const token = await getToken({ template: "canis-amnis" });
        if (!token) {
          logger.error("Failed to create canis-amnis token");
          return;
        }

        const jobSalaryClient = await StreamingJobSalaryClient.create(AppConfig.streamer.url, "business", token);
        await jobSalaryClient.start();

        if (mounted) {
          setClient(jobSalaryClient);
        }
      } catch (error) {
        logger.error("Failed to initialize JobSalaryClient:", error);
      } finally {
        setIsLoading(false);
      }
    }

    initClient().catch((error) => {
      logger.error("Failed to initialize JobSalaryClient:", error);
    });

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: JobSalaryContextType = useMemo(
    () => ({
      // Client and connection only
      client,
      isLoading,
    }),
    [client, isLoading],
  );

  return <JobSalaryContext.Provider value={contextValue}>{children}</JobSalaryContext.Provider>;
};

export const useJobSalaryContext = () => {
  const context = useContext(JobSalaryContext);
  if (context === undefined) {
    throw new Error("useJobSalaryContext must be used within a JobSalaryProvider");
  }
  return context;
};
